import {assert} from 'Asserts';
import {
  StructuredTableColumnSummary,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {
  FieldType,
  getRequiredLinkFolderItemType,
  getRequiredLinkStorableSchema,
  getRequiredLinkTagSchema,
} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

/**
 * Returns the StructuredTableColumnSummary of the leftmost source column for task and output
 * tables. Valid source column types are plate, container, and entity schema fields.
 */
export default function getColumnSummariesForProcessRelatedTable(
  structuredTable: ProcessTaskStructuredTable | ProcessOutputStructuredTable
): Array<StructuredTableColumnSummary & {columnIndex: number}> {
  assert(structuredTable.schemas.length > 0, 'Process task and output table expected to have a schema');
  const validColumnSummaries: Array<StructuredTableColumnSummary & {columnIndex: number}> = [];
  for (let idx = 0; idx < structuredTable.columns.length; idx++) {
    const column = structuredTable.columns[idx];
    const columnSchemaFieldApiId = column.schemaFieldApiId;
    const schemaField =
      columnSchemaFieldApiId != null
        ? structuredTable.schemas[0].fields.find((field) => columnSchemaFieldApiId === field.apiId)
        : null;

    if (
      column.columnType === TableColumnType.CONTAINERS ||
      column.columnType === TableColumnType.PLATE ||
      (column.columnType === TableColumnType.TAG_VALUE &&
        (column as TagValueStructuredTableColumn)?.schemaFieldType === FieldType.STORABLE_LINK)
    ) {
      const storableSchema = schemaField ? getRequiredLinkStorableSchema(schemaField) : null;
      validColumnSummaries.push({
        validDestinationColumnRequirements: [
          {
            columnType: TableColumnType.TAG_VALUE,
            schemaFieldType: FieldType.STORABLE_LINK,
            folderItemType: null,
          },
          {
            columnType: APIUtil.isPlateSchema(storableSchema?.id ?? '')
              ? TableColumnType.PLATE
              : TableColumnType.CONTAINERS,
            schemaFieldType: null,
            folderItemType: null,
          },
        ],
        schemaIdConstraint: storableSchema?.id ?? null,
        columnIndex: idx,
      });
    } else if (
      column.columnType === TableColumnType.EXISTING_BIOENTITY ||
      (column.columnType === TableColumnType.TAG_VALUE &&
        (column as TagValueStructuredTableColumn)?.schemaFieldType === FieldType.BIOENTITY_LINK)
    ) {
      const schema = schemaField ? getRequiredLinkTagSchema(schemaField) : null;
      const folderItemType = schema
        ? schema.folderItemType
        : schemaField
          ? getRequiredLinkFolderItemType(schemaField)
          : null;
      validColumnSummaries.push({
        validDestinationColumnRequirements: [
          {
            columnType: TableColumnType.TAG_VALUE,
            schemaFieldType: FieldType.BIOENTITY_LINK,
            folderItemType,
          },
          {
            columnType: TableColumnType.EXISTING_BIOENTITY,
            schemaFieldType: null,
            folderItemType,
          },
        ],
        schemaIdConstraint: schema?.id ?? null,
        columnIndex: idx,
      });
    }
  }
  return validColumnSummaries;
}
